import { DEFAULT_QUERY_OPTIONS } from "#src/components/hooks/adapters/adapterUtils";
import { useAssistant } from "#src/contexts/Assistant";
import { ExceptionUtils } from "#src/utils/exception";
import { useQueryClient } from "@tanstack/react-query";
import {
  EmbeddedReportPrompts,
  EmbeddedReportsPromptType,
  ReportV3Adapter,
  Resources,
} from "@validereinc/domain";
import React from "react";
import { AssistantPrompt } from "../AssistantPrompt";
import { useLocation } from "react-router-dom";

export const EmbeddedReportConfigurations: Record<
  EmbeddedReportsPromptType,
  { prompt: string; loadingText: string }
> = {
  [EmbeddedReportPrompts.SUMMARY]: {
    prompt: "Summarize the report",
    loadingText: "Summarizing the report for you, just a minute...",
  },
};

export const EmbeddedReportPrompt = ({
  reportId,
  reportVersion,
}: {
  reportId: string;
  reportVersion: number;
}) => {
  const queryClient = useQueryClient();
  const { setLoadingStateLabel, setContentAsync, clearContent } =
    useAssistant();
  const { pathname } = useLocation();

  const { prompt, loadingText } =
    EmbeddedReportConfigurations[EmbeddedReportPrompts.SUMMARY];

  return (
    <AssistantPrompt
      onClick={() => {
        setLoadingStateLabel(loadingText);
        setContentAsync(
          async () => {
            try {
              const summary = await queryClient.fetchQuery({
                queryKey: [
                  Resources.REPORT,
                  reportId,
                  reportVersion,
                  "embedded",
                  "summary",
                ],
                queryFn: async () => {
                  const resp = await ReportV3Adapter.embedded.summary.getOne({
                    meta: {
                      report_id: reportId,
                      report_version: reportVersion,
                      temperature: 0.3,
                      top_p: 0.6,
                    },
                  });

                  return resp.message;
                },
                ...DEFAULT_QUERY_OPTIONS,
              });

              return summary;
            } catch (err) {
              ExceptionUtils.reportException(err, "error", {
                hint: `Assistant content failed to fetch for ${reportId} v${reportVersion}`,
              });
              throw err;
            }
          },
          {
            isTiedToCurrentLocation: true,
            location: pathname,
            contentFooter: (
              <>
                <AssistantPrompt
                  onClick={() => {
                    clearContent();
                  }}
                >
                  That&#39;s all, thanks!
                </AssistantPrompt>
              </>
            ),
          }
        );
      }}
    >
      {prompt}
    </AssistantPrompt>
  );
};
